import { useFormikContext } from "formik";

import { FieldSet } from "metabase/common/components/FieldSet";
import type {
  DatabaseData,
  DatabaseFieldGroup,
  DatabaseFieldOrGroup,
  Engine,
  EngineField,
  EngineKey,
} from "metabase-types/api";
import { isContainerStyle } from "metabase-types/api";

import { isDatabaseFieldGroup, shouldShowField } from "../../utils/schema";
import { DatabaseDetailField } from "../DatabaseDetailField";

import { CustomContainer } from "./container-styles";

interface DatabaseFormBodyDetailsProps {
  fields: DatabaseFieldOrGroup[];
  autofocusFieldName?: string;
  engineKey: EngineKey | undefined;
  engine: Engine | undefined;
  isAdvanced: boolean;
}

export function DatabaseFormBodyDetails({
  fields,
  autofocusFieldName,
  engineKey,
  engine,
  isAdvanced,
}: DatabaseFormBodyDetailsProps) {
  const { values } = useFormikContext<DatabaseData>();

  function renderField(engineField: EngineField) {
    if (!shouldShowField(engineField, isAdvanced, values.details)) {
      return null;
    }

    return (
      <DatabaseDetailField
        key={engineField.name}
        field={engineField}
        autoFocus={autofocusFieldName === engineField.name}
        data-key={engineField.name}
        engineKey={engineKey}
        engine={engine}
      />
    );
  }

  function renderGroup(group: DatabaseFieldGroup) {
    const key = group.fields.map((field) => field.name).join("-");
    const container = (
      <CustomContainer key={key} containerStyle={group["container-style"]}>
        {group.fields.map(renderField)}
      </CustomContainer>
    );

    // A "grid" group lays out genuinely related fields side by side (e.g.
    // host + port) with no semantic relationship for assistive tech --
    // WCAG 1.3.1, input_fields_grouped. Wrap it in a real <fieldset> with a
    // legend built from the fields' own display names, visually hidden so
    // sighted users see no layout change. Other container styles (e.g. the
    // "backdrop" advanced-options box) aren't confirmed related-field
    // clusters, so they're left as plain containers.
    const [containerType] = isContainerStyle(group["container-style"])
      ? group["container-style"]
      : [undefined];
    if (containerType === "grid") {
      const legend = group.fields
        .map((field) => field["display-name"] ?? field.name)
        .join(", ");
      return (
        <FieldSet key={key} legend={legend} legendVisuallyHidden unstyled>
          {container}
        </FieldSet>
      );
    }

    return container;
  }

  return fields.map((field) => {
    if (isDatabaseFieldGroup(field)) {
      return renderGroup(field);
    }

    return renderField(field);
  });
}
